import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

import type {
  ITicket,
  IDefaultFilter,
  ITicketListResult,
  IAddCommentParams,
  ICommentListResult,
  IGetTicketListResponse,
  IGetCommentListResponse,
} from '../tickets.types';

const TICKET_API = {
  list: 'api/tickets/list',
  add: 'api/tickets',
  update: 'api/tickets',
  details: 'api/tickets/details',
  delete: 'api/tickets',
  import: 'api/tickets/import',
  export: 'api/tickets/export',
  listComment: 'api/tickets/comments/list',
  addComment: 'api/tickets/comments',
};

export const ticketApi = createApi({
  reducerPath: 'ticketApi',
  baseQuery,
  endpoints: (builder) => ({
    // ----------------------------------------------------------------------
    // NOTE FOR DEVELOPERS:
    // Please maintain the following order for endpoints ensuring CRUD consistency:
    // 1. List (Filter)
    // 2. Details
    // 3. Create
    // 4. Update
    // 5. Delete
    // 6. Import
    // 7. Export
    // ----------------------------------------------------------------------

    filterTicket: builder.mutation<ITicketListResult, IDefaultFilter>({
      query: (filterQuery) => ({
        url: TICKET_API.list,
        method: 'POST',
        body: filterQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: IGetTicketListResponse): ITicketListResult => {
        const list = response?.data?.list ?? [];

        const customFieldObj = (obj: any) => {
          if (obj?.customData && Object.keys(obj.customData).length > 0) {
            return obj.customData;
          }
          const cfObj = obj?.customFields?.reduce(
            (acc: any, cf: any) => {
              acc[cf.id] = cf.values;
              return acc;
            },
            {} as Record<string, any>
          );

          return cfObj || {};
        };

        const obj = {
          list: list?.map((item) => ({ ...item, customFields: customFieldObj(item) })) ?? [],
          total: response?.data?.total ?? 0,
        };

        return obj;
      },
    }),

    getTicketDtl: builder.mutation({
      query: (id: string) => ({
        url: `${TICKET_API.details}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data: ITicket }) => {
        const ticketData = { ...response.data };
        return {
          data: { ticket: ticketData },
        };
      },
    }),

    createTicket: builder.mutation<any, Partial<any>>({
      query: (params) => ({
        url: TICKET_API.add,
        method: 'POST',
        body: params,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { ticket: ITicket }; success: boolean }) =>
        response,
    }),

    updateTicket: builder.mutation<ITicket, { id: string; updates: Partial<ITicket> | any }>({
      query: ({ id, updates }) => ({
        url: `${TICKET_API.update}/${id}`,
        method: 'PUT',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { ticket: ITicket }; success: boolean }): ITicket =>
        response.success && response.data?.ticket ? response.data.ticket : ({} as ITicket),
    }),

    deleteTickets: builder.mutation({
      query: (ids: string[]) => ({
        url: TICKET_API.delete,
        method: 'DELETE',
        body: { ids },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    importTicket: builder.mutation({
      query: (filePath) => ({
        url: TICKET_API.import,
        method: 'POST',
        body: filePath,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    exportTicket: builder.mutation({
      query: (params: any) => ({
        url: TICKET_API.export,
        method: 'POST',
        body: params,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // Comment List
    getCommentList: builder.mutation<ICommentListResult, IDefaultFilter>({
      query: (filterQuery) => ({
        url: TICKET_API.listComment,
        method: 'POST',
        body: filterQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: IGetCommentListResponse) => ({
        list:
          response?.data?.list?.map((listItem) => ({
            id: listItem.id ?? '',
            userName: `${listItem.user?.firstName ?? ''} ${listItem?.user?.lastName ?? ''}`,
            ProfileURL: listItem?.user?.ProfileURL ?? '',
            content: listItem?.content ?? '',
            createdAt: listItem?.createdAt ?? '',
            attachments: listItem?.attachments ?? [],
          })) ?? [],
        total: response?.data?.total ?? 0,
      }),
    }),

    addComment: builder.mutation({
      query: (params: IAddCommentParams) => ({
        url: TICKET_API.addComment,
        method: 'POST',
        body: params,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useFilterTicketMutation,
  useGetTicketDtlMutation,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useDeleteTicketsMutation,
  useImportTicketMutation,
  useExportTicketMutation,
  // Comments
  useGetCommentListMutation,
  useAddCommentMutation,
} = ticketApi;
